import { Plus, Pencil, Trash2 } from 'lucide-react';
import type { CommentAutoReplyRule, CommentPlatform } from '@/types/outreach';
import styles from './AutoReplyRuleList.module.css';

const PLATFORM_COLORS: Record<CommentPlatform, string> = {
  FACEBOOK: styles.platformFACEBOOK,
  INSTAGRAM: styles.platformINSTAGRAM,
  TIKTOK: styles.platformTIKTOK,
};

const TRIGGER_LABELS: Record<string, string> = {
  KEYWORD: 'Keyword',
  ALL: 'All Comments',
  AI_CLASSIFIED: 'AI Classified',
};

interface AutoReplyRuleListProps {
  rules: CommentAutoReplyRule[];
  isLoading: boolean;
  onEdit: (rule: CommentAutoReplyRule) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
  deletingId?: string | null;
}

export function AutoReplyRuleList({
  rules,
  isLoading,
  onEdit,
  onDelete,
  onNew,
  deletingId,
}: AutoReplyRuleListProps) {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Auto-Reply Rules</h3>
        <button onClick={onNew} className={styles.newBtn}>
          <Plus className={styles.newIcon} />
          New Rule
        </button>
      </div>

      <div className={styles.list}>
        {isLoading && <div className={styles.empty}>Loading rules...</div>}
        {!isLoading && rules.length === 0 && (
          <div className={styles.empty}>No rules yet. Create one to automate replies.</div>
        )}
        {rules.map((rule) => (
          <div key={rule.id} className={styles.ruleCard}>
            <div className={styles.ruleLeft}>
              <div className={styles.ruleMeta}>
                <span className={`${styles.platformBadge} ${PLATFORM_COLORS[rule.platform]}`}>
                  {rule.platform}
                </span>
                <span className={styles.ruleName}>{rule.name}</span>
                <span className={styles.triggerBadge}>
                  {TRIGGER_LABELS[rule.triggerType] ?? rule.triggerType}
                </span>
              </div>
              <div className={styles.ruleFlags}>
                {rule.useAiResponse && <span className={styles.flagBadge}>AI Response</span>}
                {rule.sendPrivateDm && <span className={styles.flagBadge}>Private DM</span>}
              </div>
            </div>
            <div className={styles.ruleRight}>
              <div
                className={`${styles.activeDot} ${rule.isActive ? styles.activeDotOn : styles.activeDotOff}`}
              />
              <button onClick={() => onEdit(rule)} className={styles.iconBtn} title="Edit">
                <Pencil className={styles.iconBtnIcon} />
              </button>
              <button
                onClick={() => onDelete(rule.id)}
                className={`${styles.iconBtn} ${styles.iconBtnDelete}`}
                disabled={deletingId === rule.id}
                title="Delete"
              >
                <Trash2 className={styles.iconBtnIcon} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
